import React, { useState } from 'react';
import OptimizedImage from '@/components/OptimizedImage';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsDesktop } from '@/hooks/use-tablet';
import { useFavorites } from '@/hooks/useFavorites';
import { UniversalMap } from '@/components/maps';
import { ShareListingButton } from '@/components/ShareListingButton';
import { useUnifiedContact } from '@/contexts/UnifiedContactContext';
import { useAuth } from '@/components/AuthProvider';
import { 
  Building2, MapPin, Globe, Clock, DollarSign, Briefcase, 
  GraduationCap, Award, Check, Mail, ExternalLink, MessageCircle,
  Calendar, Users, Plane, ChevronLeft, Heart,
  FileText, Shield, Star, Lock, UserCircle, Phone
} from 'lucide-react';
import { cn } from '@/lib/utils';
import JobIcon from '@/components/JobIcon';
import { formatDistanceToNow } from 'date-fns';
import JobExperienceIndicator from './JobExperienceIndicator';
import JobQualificationBadges from './JobQualificationBadges';
import JobExperienceDetailSection from './JobExperienceDetailSection';
import JobEducationTimeline from './JobEducationTimeline';
import JobDocumentGallery from './JobDocumentGallery';
import JobReferencesList from './JobReferencesList';
import JobContractTimelineCard from './JobContractTimelineCard';
import JobPortfolioGallery from './JobPortfolioGallery';
import JobShiftPreferencesBadges from './JobShiftPreferencesBadges';
import { BlurredPrivateData } from '@/components/privacy/BlurredPrivateData';
import { useEffectiveVisibility } from '@/hooks/useEffectiveVisibility';
import { useHelicopterIcons } from '@/hooks/useHelicopterIcons';
import { useViewTracking } from '@/hooks/useViewTracking';
import { getJobTypeMapPin } from '@/utils/jobTypeIcons';
import DetailPhotoGallery from '@/components/listings/detail-modal/DetailPhotoGallery';
import BenefitsDisplayCard from '@/components/listings/detail-modal/BenefitsDisplayCard';
import AvailabilityCard from '@/components/listings/detail-modal/AvailabilityCard';
import ProfessionalLinksCard from '@/components/listings/detail-modal/ProfessionalLinksCard';
import WorkAuthorizationCard from '@/components/listings/detail-modal/WorkAuthorizationCard';
import { formatJobSalary } from '@/utils/formatJobSalary';
import { useCurrency } from '@/contexts/CurrencyContext';

// TypeRatingCards sub-component for displaying type ratings with helicopter icons
const TypeRatingCards: React.FC<{ typeRatings: string[] }> = ({ typeRatings }) => {
  const { icons: helicopterIconMatches, loading } = useHelicopterIcons(typeRatings);
  
  const findHelicopterIcon = (rating: string): string | null => {
    const match = helicopterIconMatches.find(
      m => m.typeRating.toLowerCase() === rating.toLowerCase()
    );
    return match?.iconUrl || null;
  };

  return (
    <>
      {typeRatings.map((rating, idx) => {
        const heliIcon = findHelicopterIcon(rating);
        return (
          <div 
            key={idx}
            className="flex items-center gap-3 p-3 rounded-xl bg-background border border-border shadow-sm hover:shadow-md transition-shadow"
          >
            {heliIcon ? (
              <img 
                src={heliIcon} 
                alt="" 
                className="w-10 h-10 object-contain"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center">
                <Plane className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{rating.split('(')[0].trim()}</p>
              <p className="text-[10px] text-muted-foreground">Type Rating</p>
            </div>
          </div>
        );
      })}
    </>
  );
};

interface JobListingDetailModalProps {
  listing: any;
  isOpen: boolean;
  onClose: () => void;
  onContact?: () => void;
  onRequestInfo?: () => void;
}

const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  full_time: 'Full-Time',
  part_time: 'Part-Time',
  contract: 'Contract',
  freelance: 'Freelance',
  internship: 'Internship',
  seasonal: 'Seasonal',
};

const REMOTE_OPTIONS_CONFIG: Record<string, { label: string; color: string }> = {
  onsite: { label: 'On-Site', color: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700' },
  remote: { label: 'Remote', color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-700/40' },
  hybrid: { label: 'Hybrid', color: 'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-700/40' },
};


const JobListingDetailModal: React.FC<JobListingDetailModalProps> = ({
  listing,
  isOpen,
  onClose,
  onContact,
  onRequestInfo,
}) => {
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const [activeTab, setActiveTab] = useState('overview');
  
  // Track views when modal opens
  useViewTracking(listing?.id || null, isOpen);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { openUnifiedContact } = useUnifiedContact();
  const { user } = useAuth();
  const { currency } = useCurrency();
  
  const isOwner = user?.id === listing?.user_id;
  
  const handleFavoriteToggle = async () => {
    if (listing?.id) {
      await toggleFavorite(listing.id, 'jobs');
    }
  };

  // Privacy-aware visibility
  const {
    blurPrice: shouldHidePrice,
    blurLocation: shouldHideLocation,
    blurContact: shouldHideContact,
    loading: visibilityLoading,
  } = useEffectiveVisibility(listing?.id, listing?.visibility_settings, listing?.user_id);

  const handleContact = () => {
    if (!listing) return;
    
    // If not authenticated, show toast with login prompt
    if (!user) {
      // Dispatch event to trigger auth flow
      window.dispatchEvent(new CustomEvent('require-auth', { 
        detail: { message: 'Please sign in to contact this ' + (isJobPost ? 'employer' : 'candidate') }
      }));
      return;
    }
    
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'jobs',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        contact: shouldHideContact,
      }
    });
    onClose();
  };

  const requestField = (field: string) => {
    if (!listing) return;
    if (!user) {
      window.dispatchEvent(new CustomEvent('require-auth', {
        detail: { message: 'Please sign in to request this information' }
      }));
      return;
    }
    openUnifiedContact({
      listingId: listing.id,
      listingTitle: listing.title,
      sellerId: listing.user_id,
      assetType: 'jobs',
      hiddenFields: {
        price: shouldHidePrice,
        location: shouldHideLocation,
        contact: shouldHideContact,
      },
      preselectedFields: [field],
    });
    onClose();
  };
  
  const isJobPost = listing?.listing_type === 'job_post' || listing?.listing_type === 'job_contract';
  const isJobProfile = listing?.listing_type === 'job_profile' || listing?.listing_type === 'job_seeking';
  
  if (!listing) return null;

  const heroImage = listing.hero_image || listing.company_logo;
  const hasLocation = listing.latitude && listing.longitude;
  const remoteConfig = REMOTE_OPTIONS_CONFIG[listing.remote_options || 'onsite'];

  // Shared content for both mobile and desktop
  const ModalContent = ({ useNativeScroll = false }: { useNativeScroll?: boolean }) => {
    const content = (
      <div className="pb-32">
        {/* Hero Section - Enhanced with animations */}
        <div className="relative h-52 sm:h-60 md:h-72 flex-shrink-0 bg-gradient-to-br from-primary/10 via-primary/5 to-background overflow-hidden">
          {isJobProfile ? (
            // Professional profile - larger circular photo with premium ring effect
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-800 dark:via-slate-850 dark:to-slate-900">
              {/* Decorative background circles */}
              <div className="absolute w-64 h-64 sm:w-80 sm:h-80 rounded-full bg-primary/5 blur-3xl" />
              {heroImage ? (
                <div className="relative animate-fade-in">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-primary via-emerald-500 to-primary blur-lg opacity-40 animate-pulse" style={{ transform: 'scale(1.1)' }} />
                  <div className="w-32 h-32 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full overflow-hidden ring-4 ring-white dark:ring-slate-700 shadow-2xl relative z-10">
                    <OptimizedImage 
                      src={heroImage}
                      alt={listing.title}
                      className="w-full h-full"
                      priority
                      size="medium"
                    />
                  </div>
                </div>
              ) : (
                <div className="w-32 h-32 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-full bg-background/90 shadow-2xl flex items-center justify-center ring-4 ring-background">
                  <Users className="w-14 h-14 sm:w-18 sm:h-18 md:w-20 md:h-20 text-primary/50" />
                </div>
              )}
            </div>
          ) : heroImage && heroImage !== listing.company_logo ? (
            // Real hero photo - enhanced gradient overlay
            <>
              <OptimizedImage 
                src={heroImage}
                alt={listing.title}
                className="w-full h-full"
                priority
                size="full"
                variant="hero"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent" />
            </>
          ) : listing.company_logo ? (
            // Company logo - enhanced with subtle animation
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-800 dark:via-slate-850 dark:to-slate-900 p-8">
              <div className="relative animate-fade-in">
                <div className="absolute inset-0 rounded-2xl bg-primary/10 blur-xl opacity-50" style={{ transform: 'scale(1.2)' }} />
                <div className="w-32 h-32 sm:w-36 sm:h-36 md:w-44 md:h-44 rounded-2xl bg-background shadow-xl p-5 sm:p-6 flex items-center justify-center relative z-10 border border-border">
                  <OptimizedImage 
                    src={listing.company_logo}
                    alt={listing.company_name || 'Company'}
                    className="max-h-full max-w-full"
                    size="thumbnail"
                    priority
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary/10 blur-2xl" style={{ transform: 'scale(2)' }} />
                <JobIcon category={listing.job_category} className="w-24 h-24 sm:w-28 sm:h-28 text-primary/40 relative z-10" />
              </div>
            </div>
          )}
          
          {/* Job Type Pin Icon - Top Left with enhanced styling */}
          <div className="absolute top-3 left-3 z-10">
            <div className={cn(
              "w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/95 dark:bg-slate-800/95 shadow-lg flex items-center justify-center backdrop-blur-sm transition-transform hover:scale-105",
              isJobPost ? "ring-2 ring-emerald-400 dark:ring-emerald-500" : "ring-2 ring-amber-400 dark:ring-amber-500"
            )}>
              <img src={getJobTypeMapPin(listing.listing_type)} alt="" className="w-6 h-6 sm:w-7 sm:h-7"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </div>
          </div>
          
          {/* Badges Overlay - Top Right (Desktop only, mobile has header) */}
          {!isMobile && (
            <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
              {listing.featured && (
                <Badge className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-gray-900 shadow-lg border-0 animate-pulse">
                  <Star className="w-3 h-3 mr-1 fill-current" />
                  Featured
                </Badge>
              )}
              <Button 
                variant="ghost" 
                size="icon" 
                className="bg-white/95 hover:bg-white dark:bg-slate-800/95 dark:hover:bg-slate-800 shadow-md backdrop-blur-sm h-9 w-9"
                onClick={handleFavoriteToggle}
              >
                <Heart className={cn("w-4 h-4 transition-all", isFavorite(listing.id) && "fill-red-500 text-red-500 scale-110")} />
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title}
                price={listing.salary_min || listing.salary_max || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type || 'job'}
                assetType="jobs"
                variant="ghost"
                size="icon"
                className="bg-white/95 hover:bg-white dark:bg-slate-800/95 dark:hover:bg-slate-800 shadow-md backdrop-blur-sm h-9 w-9"
              />
            </div>
          )}
          
          {/* Category Badge - Enhanced positioning */}
          <div className="absolute bottom-3 left-3 z-10">
            <Badge variant="secondary" className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-md shadow-lg border border-white/50 dark:border-slate-700/50">
              <JobIcon category={listing.job_category} className="w-4 h-4 mr-1.5" />
              <span className="capitalize font-medium">{listing.job_category?.replace(/_/g, ' ')}</span>
            </Badge>
          </div>
        </div>

        {/* Header Info Card - Enhanced with better elevation */}
        <div className="p-4 bg-gradient-to-b from-muted/50 to-transparent border-b">
          <div className="bg-background rounded-2xl border border-border p-4 sm:p-5 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Company/Profile Logo - Enhanced sizing */}
              {isJobPost && listing.company_logo && (
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-background border border-border flex items-center justify-center overflow-hidden flex-shrink-0 shadow-md">
                  <OptimizedImage 
                    src={listing.company_logo} 
                    alt={listing.company_name || 'Company'} 
                    className="w-full h-full p-1"
                    size="thumbnail"
                    priority
                  />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-foreground leading-tight">{listing.title}</h2>
                {isJobPost && listing.company_name && (
                  <p className="text-muted-foreground text-sm font-medium mt-1">{listing.company_name}</p>
                )}
                
                <div className="flex items-center gap-2 mt-2.5 flex-wrap">
                  {listing.city && !shouldHideLocation && (
                    <Badge variant="outline" className="gap-1 text-xs bg-white/50 dark:bg-slate-800/50">
                      <MapPin className="w-3 h-3" />
                      {listing.city}, {listing.country}
                    </Badge>
                  )}
                  {shouldHideLocation && listing.city && (
                    <Badge variant="outline" className="gap-1 text-xs">
                      <Lock className="w-3 h-3" />
                      <BlurredPrivateData
                        placeholderContent={<span>City, Country</span>}
                        fieldName="Location"
                        onRequestInfo={isOwner ? undefined : () => requestField('location')}
                        size="sm"
                        inline
                      />
                    </Badge>
                  )}
                  {listing.remote_options && (
                    <Badge variant="outline" className={cn("gap-1 text-xs", remoteConfig.color)}>
                      <Globe className="w-3 h-3" />
                      {remoteConfig.label}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats Grid - Enhanced with better gradients and hover effects */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mt-5">
              <div className="p-3 sm:p-3.5 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-50/70 dark:from-emerald-950/50 dark:via-green-950/40 dark:to-emerald-950/30 rounded-xl border border-emerald-200/70 dark:border-emerald-700/50 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
                <div className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400 mb-1">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Salary</span>
                </div>
                {shouldHidePrice ? (
                  <BlurredPrivateData
                    placeholderContent={<span className="font-bold text-sm text-emerald-800 dark:text-emerald-300">$XX,XXX/yr</span>}
                    fieldName="Salary"
                    onRequestInfo={isOwner ? undefined : () => requestField('price')}
                    size="sm"
                  />
                ) : (
                  <div className="font-bold text-sm text-emerald-800 dark:text-emerald-300">
                    {formatJobSalary(listing.salary_min, listing.salary_max, listing.salary_type, 'Competitive', currency)}
                  </div>
                )}
              </div>
              
              <div className="p-3 sm:p-3.5 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-50/70 dark:from-blue-950/50 dark:via-indigo-950/40 dark:to-blue-950/30 rounded-xl border border-blue-200/70 dark:border-blue-700/50 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
                <div className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Type</span>
                </div>
                <div className="font-bold text-sm text-blue-800 dark:text-blue-300">
                  {EMPLOYMENT_TYPE_LABELS[listing.employment_type] || listing.employment_type || 'Full-Time'}
                </div>
              </div>
              
              <div className="p-3 sm:p-3.5 bg-gradient-to-br from-violet-50 via-purple-50 to-violet-50/70 dark:from-violet-950/50 dark:via-purple-950/40 dark:to-violet-950/30 rounded-xl border border-violet-200/70 dark:border-violet-700/50 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
                <div className="flex items-center gap-1.5 text-violet-700 dark:text-violet-400 mb-1">
                  <GraduationCap className="w-4 h-4" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Experience</span>
                </div>
                <div className="font-bold text-sm text-violet-800 dark:text-violet-300">
                  {listing.experience_years_min ? `${listing.experience_years_min}+ years` : 'Any Level'}
                </div>
              </div>
              
              <div className="p-3 sm:p-3.5 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50/70 dark:from-slate-800/50 dark:via-gray-800/40 dark:to-slate-800/30 rounded-xl border border-slate-200/70 dark:border-slate-700/50 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
                <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-400 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span className="text-[10px] font-semibold uppercase tracking-wide">Posted</span>
                </div>
                <div className="font-bold text-sm text-slate-700 dark:text-slate-300">
                  {listing.created_at ? formatDistanceToNow(new Date(listing.created_at), { addSuffix: true }) : 'Recently'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col">
          <TabsList className="w-full justify-start px-4 py-2 border-b bg-muted/30 rounded-none flex-shrink-0 overflow-x-auto scrollbar-hide after:content-[''] after:w-4 after:flex-shrink-0">
            <TabsTrigger value="overview" className="gap-1.5 flex-shrink-0 text-xs sm:text-sm">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="requirements" className="gap-1.5 flex-shrink-0 text-xs sm:text-sm">
              <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>{isJobProfile ? 'Qualifications' : 'Requirements'}</span>
            </TabsTrigger>
            {isJobProfile && (listing.pilot_experience || listing.mechanic_experience || listing.education?.length > 0 || listing.experience_summary) && (
              <TabsTrigger value="experience" className="gap-1.5 flex-shrink-0 text-xs sm:text-sm">
                <UserCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>Experience</span>
                {listing.education?.length > 0 && (
                  <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                    {listing.education.length}
                  </Badge>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="company" className="gap-1.5 flex-shrink-0 text-xs sm:text-sm">
              {isJobPost ? <Building2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
              <span>{isJobPost ? 'Company' : 'Profile'}</span>
              {isJobProfile && (listing.documents?.length > 0 || listing.references_data?.length > 0) && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                  {(listing.documents?.length || 0) + (listing.references_data?.length || 0)}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <div className="p-4 space-y-4">
            {/* Overview Tab */}
            <TabsContent value="overview" className="mt-0 space-y-4">
              {/* Photo Gallery - NEW */}
              {listing.photos?.length > 0 && (
                <DetailPhotoGallery 
                  photos={listing.photos} 
                  title={isJobPost ? "Workplace Photos" : "Portfolio Photos"}
                />
              )}

              {listing.description && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-primary" />
                      {isJobPost ? 'Job Description' : 'About Me'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.description}
                    </p>
                  </CardContent>
                </Card>
              )}

              {isJobPost && listing.responsibilities && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Check className="w-4 h-4 text-primary" />
                      Key Responsibilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.responsibilities}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Selected Benefits - NEW (detailed display) */}
              {listing.selected_benefits?.length > 0 && (
                <BenefitsDisplayCard benefits={listing.selected_benefits} />
              )}

              {/* Legacy benefits text fallback */}
              {isJobPost && listing.benefits && !listing.selected_benefits?.length && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Heart className="w-4 h-4 text-primary" />
                      Benefits & Perks
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.benefits}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Availability Card - NEW (for job profiles) */}
              {isJobProfile && (
                <AvailabilityCard
                  availableFrom={listing.available_from}
                  noticePeriod={listing.notice_period}
                  currentPosition={listing.current_position}
                  preferredLocations={listing.preferred_locations}
                  willingToRelocate={listing.willing_to_relocate}
                />
              )}
              
              {/* Relocation & Benefits Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {listing.relocation_offered && (
                  <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0 gap-1">
                    <Plane className="w-3 h-3" />
                    Relocation Assistance
                  </Badge>
                )}
                {listing.includes_benefits && !listing.selected_benefits?.length && (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-0 gap-1">
                    <Briefcase className="w-3 h-3" />
                    Benefits Included
                  </Badge>
                )}
              </div>
            </TabsContent>

            {/* Requirements Tab */}
            <TabsContent value="requirements" className="mt-0 space-y-4">
              {/* Type Ratings - TOP PRIORITY with Helicopter Icons */}
              {listing.type_ratings?.length > 0 && (
                <Card className="border-violet-200/60 dark:border-violet-700/40 bg-gradient-to-br from-violet-50/50 to-purple-50/30 dark:from-violet-900/20 dark:to-purple-900/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Plane className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                      Type Ratings
                      <Badge variant="secondary" className="text-[10px] ml-auto bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
                        {isJobPost ? 'Required' : 'Qualified'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                      <TypeRatingCards typeRatings={listing.type_ratings} />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Experience Indicators */}
              {(listing.experience_years_min || listing.flight_hours_min) && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <GraduationCap className="w-4 h-4 text-primary" />
                      Experience Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <JobExperienceIndicator 
                      experienceYears={listing.experience_years_min}
                      flightHours={listing.flight_hours_min}
                    />
                  </CardContent>
                </Card>
              )}

              {listing.requirements && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <FileText className="w-4 h-4 text-primary" />
                      Requirements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {listing.requirements}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Other Qualifications (Licenses, Certifications) */}
              {(listing.licenses?.length > 0 || listing.certifications?.length > 0) && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Award className="w-4 h-4 text-primary" />
                      {isJobPost ? 'Required Qualifications' : 'Qualifications'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <JobQualificationBadges 
                      licenses={listing.licenses || []}
                      certifications={listing.certifications || []}
                      typeRatings={[]} // Type ratings shown separately above
                      skills={listing.skills || []}
                    />
                  </CardContent>
                </Card>
              )}

              {/* Work Authorization & Medical Class - NEW */}
              <WorkAuthorizationCard
                workAuthorization={listing.work_authorization}
                mode={isJobPost ? 'employer' : 'candidate'}
                medicalClass={listing.medical_class}
              />

              {listing.security_clearance && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary" />
                      Security Clearance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="outline" className="bg-slate-50 dark:bg-slate-800">
                      {listing.security_clearance}
                    </Badge>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Experience Tab (Professionals Only) - Consolidated */}
            {isJobProfile && (
              <TabsContent value="experience" className="mt-0 space-y-5">
                {/* Contract Timeline (for job_seeking/contract types) */}
                {(listing.contract_start_date || listing.project_duration || listing.urgency_level) && (
                  <JobContractTimelineCard
                    contractStartDate={listing.contract_start_date}
                    contractEndDate={listing.contract_end_date}
                    projectDuration={listing.project_duration}
                    urgencyLevel={listing.urgency_level}
                  />
                )}

                {/* Detailed Experience Section */}
                <JobExperienceDetailSection
                  category={listing.job_category}
                  pilotExperience={listing.pilot_experience}
                  mechanicExperience={listing.mechanic_experience}
                  experienceSummary={listing.experience_summary}
                  keyAchievements={listing.key_achievements}
                />

                {/* Shift Preferences */}
                {listing.shift_preferences?.length > 0 && (
                  <Card className="border-border">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold flex items-center gap-2">
                        <Clock className="w-4 h-4 text-primary" />
                        Schedule Preferences
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <JobShiftPreferencesBadges preferences={listing.shift_preferences} />
                    </CardContent>
                  </Card>
                )}

                {/* Education Timeline */}
                {listing.education?.length > 0 && (
                  <JobEducationTimeline education={listing.education} />
                )}

                {/* Portfolio Gallery */}
                {listing.portfolio_items?.length > 0 && (
                  <JobPortfolioGallery items={listing.portfolio_items} />
                )}
              </TabsContent>
            )}

            {/* Company/Profile Tab */}
            <TabsContent value="company" className="mt-0 space-y-4">
              {isJobPost && (
                <>
                  {listing.company_description && (
                    <Card className="border-border">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-primary" />
                          About {listing.company_name || 'the Company'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {listing.company_description}
                        </p>
                        
                        {listing.company_size && (
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="w-4 h-4 text-muted-foreground" />
                            <span className="capitalize">{listing.company_size.replace(/_/g, ' ')} Company</span>
                          </div>
                        )}
                        
                        {listing.company_website && (
                          <a 
                            href={listing.company_website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1"
                          >
                            <Globe className="w-4 h-4" />
                            Visit Website
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {/* Location Map */}
              {hasLocation && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-primary" />
                      Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {shouldHideLocation ? (
                      <div className="relative">
                        <div className="h-40 sm:h-48 rounded-lg overflow-hidden blur-sm pointer-events-none">
                          <UniversalMap
                            markers={[{
                              id: listing.id,
                              position: { lat: listing.latitude, lng: listing.longitude },
                              title: listing.title,
                              manufacturer: listing.company_name || 'Job',
                              model: listing.job_category,
                              listingType: listing.listing_type,
                            }]}
                            height="h-full"
                            mode="interactive"
                            assetType="jobs"
                          />
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-background/30 backdrop-blur-[1px] rounded-lg">
                          <BlurredPrivateData
                            placeholderContent={<span>Map Location</span>}
                            fieldName="Location"
                            onRequestInfo={isOwner ? undefined : () => requestField('location')}
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="h-40 sm:h-48 rounded-lg overflow-hidden">
                          <UniversalMap
                            markers={[{
                              id: listing.id,
                              position: { lat: listing.latitude, lng: listing.longitude },
                              title: listing.title,
                              manufacturer: listing.company_name || 'Job',
                              model: listing.job_category,
                              listingType: listing.listing_type,
                            }]}
                            height="h-full"
                            mode="interactive"
                            assetType="jobs"
                          />
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          {listing.city}, {listing.country}
                        </p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Contact Information */}
              {(listing.contact_name || listing.contact_email || listing.contact_phone) && (
                <Card className="border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary" />
                      Contact Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {listing.contact_name && (
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="w-4 h-4 text-muted-foreground" />
                        <span>{listing.contact_name}</span>
                      </div>
                    )}
                    {shouldHideContact ? (
                      <BlurredPrivateData
                        placeholderContent={<span>Contact details hidden</span>}
                        fieldName="Contact"
                        onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                        size="sm"
                      />
                    ) : (
                      <>
                        {listing.contact_email && (
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span>{listing.contact_email}</span>
                          </div>
                        )}
                        {listing.contact_phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span>{listing.contact_phone}</span>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Professional Links & Documents - MERGED into Profile tab */}
              {isJobProfile && (
                <>
                  <ProfessionalLinksCard
                    linkedinUrl={listing.linkedin_url}
                    portfolioUrl={listing.portfolio_url}
                    resumeUrl={listing.resume_url}
                    websiteUrl={listing.company_website}
                    isHidden={shouldHideContact}
                    onRequestInfo={isOwner ? undefined : () => requestField('contact')}
                  />

                  {/* Documents Gallery - Moved from Documents tab */}
                  {listing.documents?.length > 0 && (
                    <JobDocumentGallery 
                      documents={listing.documents}
                      onRequestAccess={isOwner ? undefined : () => requestField('documents')}
                    />
                  )}

                  {/* References - Moved from Documents tab */}
                  {listing.references_data?.length > 0 && (
                    <JobReferencesList 
                      references={listing.references_data}
                      onRequestAccess={isOwner ? undefined : () => requestField('references')}
                    />
                  )}
                </>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    );
    
    if (useNativeScroll) {
      return <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">{content}</div>;
    }
    return <ScrollArea className="flex-1 min-h-0">{content}</ScrollArea>;
  };

  // Fixed Bottom CTA
  const BottomCTA = () => (
    <div className="flex-shrink-0 p-3 sm:p-4 border-t bg-background/95 backdrop-blur-sm">
      <div className="flex gap-2 sm:gap-3">
        <Button 
          onClick={handleContact} 
          disabled={isOwner} 
          className={cn("flex-1", isOwner ? "bg-muted text-muted-foreground cursor-not-allowed hover:bg-muted" : "bg-foreground hover:bg-foreground/90 text-background")} 
          size="lg"
        >
          <MessageCircle className="w-4 h-4 mr-2" />
          {isOwner ? 'This is your listing' : isJobPost ? 'Apply Now' : 'Contact Candidate'}
        </Button>
        {isJobProfile && !isOwner && (
          <Button 
            variant="outline"
            size="lg"
            onClick={(e) => {
              e.stopPropagation();
              const event = new CustomEvent('open-job-offer-response', { detail: { request: listing } });
              window.dispatchEvent(event);
            }}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Offer Position
          </Button>
        )}
        <Button variant="outline" size="lg" className="px-3 sm:px-4" onClick={handleFavoriteToggle} aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
          <Heart className={cn("w-4 h-4", isFavorite(listing.id) && "fill-red-500 text-red-500")} />
        </Button>
        <ShareListingButton
          listingId={listing.id}
          title={listing.title}
          price={listing.salary_min || listing.salary_max || 0}
          location={[listing.city, listing.country].filter(Boolean).join(', ')}
          listingType={listing.listing_type || 'job'}
          assetType="jobs"
          variant="outline"
          size="sm"
          className="px-3 sm:px-4"
        />
      </div>
    </div>
  );

  // Mobile: Full-height Drawer with sticky header (helicopter pattern)
  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()} handleOnly={true}>
        <DrawerContent className="h-[100svh] max-h-[100svh] flex flex-col z-[100]" showCloseButton={false} showHandle={false}>
          {/* Sticky Back Button Header - helicopter pattern */}
          <div className="sticky top-0 z-50 bg-background border-b border-border px-3 py-2.5 flex items-center gap-3 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-9 w-9 flex-shrink-0 hover:bg-muted"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <DrawerTitle className="sr-only">{isJobPost ? 'Job Details' : 'Candidate Profile'}</DrawerTitle>
            <DrawerDescription className="sr-only">
              {isJobPost ? 'Job listing' : 'Candidate profile'} for {listing.title}
            </DrawerDescription>
            <span className="font-medium text-sm text-foreground/80 truncate flex-1">
              {isJobPost ? 'Job Details' : 'Candidate Profile'}
            </span>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={handleFavoriteToggle} aria-label={isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}>
                <Heart className={cn("w-4 h-4", isFavorite(listing.id) && "fill-red-500 text-red-500")} />
              </Button>
              <ShareListingButton
                listingId={listing.id}
                title={listing.title}
                price={listing.salary_min || listing.salary_max || 0}
                location={[listing.city, listing.country].filter(Boolean).join(', ')}
                listingType={listing.listing_type || 'job'}
                assetType="jobs"
                variant="ghost"
                size="icon"
                className="h-9 w-9"
              />
            </div>
          </div>
          
          <ModalContent useNativeScroll />
          <BottomCTA />
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: Dialog modal
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl h-[90vh] max-h-[90vh] overflow-hidden p-0 flex flex-col">
        <div className="px-6 pt-4 pb-2 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-lg font-semibold">
                {isJobPost ? 'Job Details' : 'Candidate Profile'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {isJobPost ? 'Job listing' : 'Candidate profile'} for {listing.title}
              </DialogDescription>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFavoriteToggle}
                aria-label={listing && isFavorite(listing.id) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Heart className={cn("w-4 h-4", listing && isFavorite(listing.id) && "fill-red-500 text-red-500")} />
              </Button>
              {listing && (
                <ShareListingButton
                  listingId={listing.id}
                  title={listing.title}
                  price={listing.salary_min || 0}
                  location={listing.city && listing.country ? `${listing.city}, ${listing.country}` : listing.country || ''}
                  listingType={listing.listing_type}
                  variant="ghost"
                  size="icon"
                />
              )}
            </div>
          </div>
        </div>
        <ModalContent />
        <BottomCTA />
      </DialogContent>
    </Dialog>
  );
};

export default JobListingDetailModal;
